import type { InferOutput } from "valibot";

declare global { const cart: Cart; }

// Cart item
export const cartItemSchema = v.object({
  key: v.string(),
  product: productDocumentSchema,
  details: v.array(v.string()),
  quantity: v.number(),
  stock_level: v.number(),
  subtotal: v.number(),
});
export type CartItem = InferOutput<typeof cartItemSchema>;

// Cart goal
export const cartGoalSchema = v.variant("goal_type", [
  v.object({
    amount: v.number(),
    goal_type: v.literal("free_shipping"),
  }),
  v.object({
    amount: v.number(),
    goal_type: v.literal("lillopoints"),
    lillopoints: v.number(),
  }),
]);
export type CartGoal = InferOutput<typeof cartGoalSchema>;

// Cart Lillopoints
export const cartLillopointsSchema = v.object({
  is_applied: v.boolean(),
  conversion_rate: v.number(),
  max_points: v.number(),
  total_points: v.number(),
});
export type CartLillopoints = InferOutput<typeof cartLillopointsSchema>;

// Shipping method
export const shippingMethodSchema = v.object({
  method_id: v.string(),
  label: v.string(),
  cost: v.number(),
  url: v.optional(v.pipe(v.string(), v.url())),
});
export type ShippingMethod = InferOutput<typeof shippingMethodSchema>;

// Shipping package
export const shippingPackageSchema = v.object({
  package_key: v.number(),
  selected_method_id: v.string(),
  available_methods: v.array(shippingMethodSchema),
});
export type ShippingPackage = InferOutput<typeof shippingPackageSchema>;

// Cart shipping
export const cartShippingSchema = v.object({
  packages: v.array(shippingPackageSchema),
});
export type CartShipping = InferOutput<typeof cartShippingSchema>;

// Cart totals
export const cartTotalsSchema = v.object({
  subtotal: v.number(),
  shipping: v.number(),
  coupons: v.record(v.string(), v.number()),
  fees: v.record(v.string(), v.number()),
  taxes: v.record(v.string(), v.number()),
  total: v.number(),
});
export type CartTotals = InferOutput<typeof cartTotalsSchema>;

// Cart
export const cartSchema = v.object({
  customer: customerSchema,
  cart_items: v.array(cartItemSchema),
  goals: v.array(cartGoalSchema),
  lillopoints: cartLillopointsSchema,
  shipping: cartShippingSchema,
  totals: cartTotalsSchema,
});
export type Cart = InferOutput<typeof cartSchema>;

// 'add_to_cart' input
export const addToCartSchema = v.object({
  product_id: v.number(),
  quantity: v.number(),
});
export type AddToCart = InferOutput<typeof addToCartSchema>;

// 'remove_from_cart' input
export const removeFromCartSchema = v.object({
  cart_item_key: v.string(),
});
export type RemoveFromCart = InferOutput<typeof removeFromCartSchema>;

// 'update_cart_item' input
export const updateCartItemSchema = v.object({
  cart_item_key: v.string(),
  quantity: v.number(),
});
export type UpdateCartItem = InferOutput<typeof updateCartItemSchema>;

// 'apply_coupon' input
export const applyCouponSchema = v.object({
  code: v.string(),
});
export type ApplyCoupon = InferOutput<typeof applyCouponSchema>;

// 'remove_coupon' input
export const removeCouponSchema = v.object({
  code: v.string(),
});
export type RemoveCoupon = InferOutput<typeof removeCouponSchema>;

// 'choose_shipping_method' input
export const chooseShippingMethodSchema = v.object({
  packages: v.array(v.omit(shippingPackageSchema, ["available_methods"])),
});
export type ChooseShippingMethod = InferOutput<typeof chooseShippingMethodSchema>;

// 'apply_lillopoints' input
export const applyLillopointsSchema = v.object({
  points: v.number(),
});
export type ApplyLillopoints = InferOutput<typeof applyLillopointsSchema>;
